import React, { useCallback, useEffect, useState } from "react";
import {
  Edit3,
  Loader2,
  Plus,
  RefreshCw,
  Store,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";
import { BulkMenus, BulkMenuPackageMappings } from "./BulkPackages";
import {
  BulkPackageList,
  BulkPackageVariants,
  BulkPackageItemMappings,
} from "./BulkPackageManagement";
import BulkOptionGroups from "./BulkOptionGroups";
import BulkPackageOptionGroups from "./BulkPackageOptionGroups";
import BulkOptionGroupItems from "./BulkOptionGroupItems";
import {
  BulkPackageGroupSets,
  BulkPackageGroupSetMembers,
} from "./BulkPackageGroupSets";
import { BulkAddons, BulkPackageAddons } from "./BulkAddons";

const MANAGEMENT_TABS = [
  { id: "restaurant", label: "Restaurant" },
  { id: "category", label: "Category" },
  { id: "catalogue", label: "Catalogue" },
  { id: "item", label: "Items & Pricing" },
  { id: "bulk-menu", label: "Bulk Menu" },
  { id: "menu-package", label: "Menu Package Mapping" },
  { id: "packages", label: "Packages" },
  { id: "package-variants", label: "Package Variants" },
  { id: "package-items", label: "Package Item Mapping" },
  { id: "option-groups", label: "Option Groups" },
  { id: "package-option-groups", label: "Package Option Groups" },
  { id: "option-group-items", label: "Option Group Items" },
  { id: "package-group-sets", label: "Package Group Sets" },
  { id: "package-group-set-members", label: "Group Set Members" },
  { id: "addons", label: "Addons" },
  { id: "package-addons", label: "Package Addons" },
];

export default function BulkRestaurants() {
  const [activeManagementTab, setActiveManagementTab] = useState("restaurant");

  return (
    <div className=" w-full max-w-8xl space-y-3">
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
        <div className="flex min-w-max items-center gap-1">
          {MANAGEMENT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveManagementTab(tab.id)}
              className={`h-9 rounded-lg px-4 text-xs font-semibold transition ${
                activeManagementTab === tab.id
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeManagementTab === "restaurant" && <BulkRestaurantsContent />}
      {activeManagementTab === "category" && <BulkItemCategories />}
      {activeManagementTab === "catalogue" && <BulkCatalogues />}
      {activeManagementTab === "item" && <BulkItemManagement />}
      {activeManagementTab === "bulk-menu" && <BulkMenus />}
      {activeManagementTab === "menu-package" && <BulkMenuPackageMappings />}
      {activeManagementTab === "packages" && <BulkPackageList />}
      {activeManagementTab === "package-variants" && <BulkPackageVariants />}
      {activeManagementTab === "package-items" && <BulkPackageItemMappings />}
      {activeManagementTab === "option-groups" && <BulkOptionGroups />}
      {activeManagementTab === "package-option-groups" && (
        <BulkPackageOptionGroups />
      )}
      {activeManagementTab === "option-group-items" && <BulkOptionGroupItems />}
      {activeManagementTab === "package-group-sets" && <BulkPackageGroupSets />}
      {activeManagementTab === "package-group-set-members" && (
        <BulkPackageGroupSetMembers />
      )}
      {activeManagementTab === "addons" && <BulkAddons />}
      {activeManagementTab === "package-addons" && <BulkPackageAddons />}
    </div>
  );
}

const EMPTY_FORM = {
  restaurant_id: "",
  restaurant_name: "",
  phone: "",
  status: "ACTIVE",
};

function BulkRestaurantsContent() {
  const {
    restaurantSaving,
    restaurantsLoading,
    restaurantDeletingId,
    getRestaurants,
    insertRestaurant,
    updateRestaurant,
    deleteRestaurant,
  } = useBulkCatalogue();
  const [restaurants, setRestaurants] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadRestaurants = useCallback(async () => {
    const rows = await getRestaurants("");
    setRestaurants(Array.isArray(rows) ? rows : []);
  }, [getRestaurants]);

  useEffect(() => {
    loadRestaurants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const openAdd = () => {
    setEditing({ mode: "ADD" });
    setForm(EMPTY_FORM);
  };
  const openEdit = (restaurant) => {
    setEditing({ mode: "EDIT" });
    setForm({
      restaurant_id: String(restaurant?.restaurant_id ?? restaurant?.id ?? ""),
      restaurant_name: String(restaurant?.restaurant_name ?? ""),
      phone: String(restaurant?.phone ?? ""),
      status: String(restaurant?.status ?? "ACTIVE"),
    });
  };
  const closeModal = () => {
    if (!restaurantSaving) {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success =
      editing?.mode === "EDIT"
        ? await updateRestaurant(form)
        : await insertRestaurant(form);
    if (success) {
      closeModal();
      await loadRestaurants();
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.restaurant_id ?? deleteTarget?.id;
    if (!id) return;
    const success = await deleteRestaurant(id);
    if (success) {
      setDeleteTarget(null);
      await loadRestaurants();
    }
  };

  return (
    <div className="w-full max-w-8xl space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
            <Store size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Bulk Restaurants
            </h3>
            <p className="text-[11px] text-slate-500">
              Add, edit and delete Bulk Catalogue restaurants.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadRestaurants}
            disabled={restaurantsLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={restaurantsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"
          >
            <Plus size={13} />
            Add Restaurant
          </button>
        </div>
      </div>

      {restaurantsLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : restaurants.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-14 text-center">
          <Store size={26} className="mx-auto text-slate-300" />
          <p className="mt-2 text-sm font-semibold text-slate-800">
            No restaurants found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Add your first Bulk Catalogue restaurant.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {restaurants.map((restaurant, index) => {
            const id = restaurant?.restaurant_id ?? restaurant?.id ?? index;
            const active =
              String(restaurant?.status || "").toUpperCase() === "ACTIVE";
            return (
              <div
                key={id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
                  <Store size={15} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {restaurant?.restaurant_name || "Unnamed restaurant"}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
                    >
                      {restaurant?.status || "-"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    ID #{id} · {restaurant?.phone || "No phone"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(restaurant)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600"
                  title="Edit"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(restaurant)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 hover:bg-rose-100"
                  title="Delete"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT"
                    ? "Edit Restaurant"
                    : "Add Restaurant"}
                </h3>
                <p className="text-xs text-slate-500">
                  Bulk Catalogue restaurant details
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={restaurantSaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant Name
                  </span>
                  <input
                    name="restaurant_name"
                    value={form.restaurant_name}
                    onChange={handleChange}
                    disabled={restaurantSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Phone
                  </span>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    disabled={restaurantSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={restaurantSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={restaurantSaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={restaurantSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {restaurantSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {restaurantSaving
                    ? "Saving..."
                    : editing.mode === "EDIT"
                      ? "Update Restaurant"
                      : "Add Restaurant"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                <Trash2 size={17} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Delete Restaurant
                </h3>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Delete{" "}
                  <b>{deleteTarget?.restaurant_name || "this restaurant"}</b>?
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(restaurantDeletingId)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(restaurantDeletingId)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {restaurantDeletingId ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const EMPTY_CATALOGUE_FORM = {
  catalogue_id: "",
  restaurant_id: "",
  catalogue_name: "",
  description: "",
  valid_from: "",
  valid_to: "",
  status: "ACTIVE",
};

function BulkCatalogues() {
  const {
    getRestaurants,
    getCatalogues,
    insertCatalogue,
    updateCatalogue,
    deleteCatalogue,
    catalogueSaving,
    cataloguesLoading,
    catalogueDeletingId,
  } = useBulkCatalogue();

  const [restaurants, setRestaurants] = useState([]);
  const [catalogues, setCatalogues] = useState([]);
  const [form, setForm] = useState(EMPTY_CATALOGUE_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadCatalogues = useCallback(async () => {
    const [catalogueRows, restaurantRows] = await Promise.all([
      getCatalogues(""),
      getRestaurants(""),
    ]);
    setCatalogues(Array.isArray(catalogueRows) ? catalogueRows : []);
    setRestaurants(Array.isArray(restaurantRows) ? restaurantRows : []);
  }, [getCatalogues, getRestaurants]);

  useEffect(() => {
    loadCatalogues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureRestaurants = async () => {
    if (restaurants.length) return restaurants;
    const rows = await getRestaurants("");
    const next = Array.isArray(rows) ? rows : [];
    setRestaurants(next);
    return next;
  };

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const openAdd = async () => {
    const restaurantRows = await ensureRestaurants();
    setEditing({ mode: "ADD" });
    setForm({
      ...EMPTY_CATALOGUE_FORM,
      restaurant_id: String(
        restaurantRows?.[0]?.restaurant_id ?? restaurantRows?.[0]?.id ?? "",
      ),
    });
  };

  const openEdit = async (catalogue) => {
    await ensureRestaurants();
    setEditing({ mode: "EDIT" });
    setForm({
      catalogue_id: String(catalogue?.catalogue_id ?? catalogue?.id ?? ""),
      restaurant_id: String(catalogue?.restaurant_id ?? ""),
      catalogue_name: String(catalogue?.catalogue_name ?? ""),
      description: String(catalogue?.description ?? ""),
      valid_from: String(catalogue?.valid_from ?? ""),
      valid_to: String(catalogue?.valid_to ?? ""),
      status: String(catalogue?.status ?? "ACTIVE"),
    });
  };

  const closeModal = () => {
    if (catalogueSaving) return;
    setEditing(null);
    setForm(EMPTY_CATALOGUE_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success =
      editing?.mode === "EDIT"
        ? await updateCatalogue(form)
        : await insertCatalogue(form);
    if (success) {
      closeModal();
      await loadCatalogues();
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.catalogue_id ?? deleteTarget?.id;
    if (!id) return;
    const success = await deleteCatalogue(id);
    if (success) {
      setDeleteTarget(null);
      await loadCatalogues();
    }
  };

  const restaurantName = (restaurantId) => {
    const row = restaurants.find(
      (item) =>
        String(item?.restaurant_id ?? item?.id ?? "") ===
        String(restaurantId ?? ""),
    );
    return row?.restaurant_name || `Restaurant #${restaurantId || "—"}`;
  };

  return (
    <div className="space-y-3 pt-2">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Bulk Catalogues
          </h3>
          <p className="text-[11px] text-slate-500">
            Manage catalogues for the restaurants above.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadCatalogues}
            disabled={cataloguesLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={cataloguesLoading ? "animate-spin" : ""}
            />{" "}
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700"
          >
            <Plus size={13} /> Add Catalogue
          </button>
        </div>
      </div>

      {cataloguesLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : catalogues.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center">
          <p className="text-sm font-semibold text-slate-800">
            No catalogues found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Add a catalogue and map it to a restaurant.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {catalogues.map((catalogue, index) => {
            const id = catalogue?.catalogue_id ?? catalogue?.id ?? index;
            const active =
              String(catalogue?.status || "").toUpperCase() === "ACTIVE";
            return (
              <div
                key={id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {catalogue?.catalogue_name || "Unnamed catalogue"}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
                    >
                      {catalogue?.status || "-"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    ID #{id} · {restaurantName(catalogue?.restaurant_id)} ·{" "}
                    {catalogue?.valid_from || "—"} to{" "}
                    {catalogue?.valid_to || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(catalogue)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-blue-50 hover:text-blue-600"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(catalogue)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 hover:bg-rose-100"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT" ? "Edit Catalogue" : "Add Catalogue"}
                </h3>
                <p className="text-xs text-slate-500">Bulk Catalogue details</p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={catalogueSaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant
                  </span>
                  <select
                    name="restaurant_id"
                    value={form.restaurant_id}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="">Select Restaurant</option>
                    {restaurants.map((restaurant) => {
                      const id = restaurant?.restaurant_id ?? restaurant?.id;
                      return (
                        <option key={id} value={id}>
                          {restaurant?.restaurant_name || `Restaurant #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Catalogue Name
                  </span>
                  <input
                    name="catalogue_name"
                    value={form.catalogue_name}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Valid From
                  </span>
                  <input
                    name="valid_from"
                    type="date"
                    value={form.valid_from}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Valid To
                  </span>
                  <input
                    name="valid_to"
                    type="date"
                    value={form.valid_to}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </span>
                  <textarea
                    name="description"
                    rows={3}
                    value={form.description}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={catalogueSaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={catalogueSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {catalogueSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {catalogueSaving
                    ? "Saving..."
                    : editing.mode === "EDIT"
                      ? "Update Catalogue"
                      : "Add Catalogue"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              Delete Catalogue
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Delete <b>{deleteTarget?.catalogue_name || "this catalogue"}</b>?
              This action cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(catalogueDeletingId)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(catalogueDeletingId)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {catalogueDeletingId ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}{" "}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const EMPTY_ITEM_CATEGORY_FORM = {
  category_id: "",
  restaurant_id: "",
  parent_category_id: "",
  category_name: "",
  description: "",
  display_order: "",
  status: "ACTIVE",
};

function BulkItemCategories() {
  const {
    getRestaurants,
    getItemCategories,
    insertItemCategory,
    updateItemCategory,
    deleteItemCategory,
    itemCategorySaving,
    itemCategoriesLoading,
    itemCategoryDeletingId,
  } = useBulkCatalogue();

  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_ITEM_CATEGORY_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadData = async () => {
    const [categoryRows, restaurantRows] = await Promise.all([
      getItemCategories(""),
      getRestaurants(""),
    ]);
    setCategories(Array.isArray(categoryRows) ? categoryRows : []);
    setRestaurants(Array.isArray(restaurantRows) ? restaurantRows : []);
  };

  const ensureRestaurants = async () => {
    if (restaurants.length) return restaurants;
    const rows = await getRestaurants("");
    const next = Array.isArray(rows) ? rows : [];
    setRestaurants(next);
    return next;
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const openAdd = async () => {
    const restaurantRows = await ensureRestaurants();
    setEditing({ mode: "ADD" });
    setForm({
      ...EMPTY_ITEM_CATEGORY_FORM,
      restaurant_id: String(
        restaurantRows?.[0]?.restaurant_id ?? restaurantRows?.[0]?.id ?? "",
      ),
    });
  };

  const openEdit = async (category) => {
    await ensureRestaurants();
    setEditing({ mode: "EDIT" });
    setForm({
      category_id: String(category?.category_id ?? category?.id ?? ""),
      restaurant_id: String(category?.restaurant_id ?? ""),
      parent_category_id: String(category?.parent_category_id ?? ""),
      category_name: String(category?.category_name ?? ""),
      description: String(category?.description ?? ""),
      display_order: String(category?.display_order ?? ""),
      status: String(category?.status ?? "ACTIVE"),
    });
  };

  const closeModal = () => {
    if (itemCategorySaving) return;
    setEditing(null);
    setForm(EMPTY_ITEM_CATEGORY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success =
      editing?.mode === "EDIT"
        ? await updateItemCategory(form)
        : await insertItemCategory(form);
    if (success) {
      closeModal();
      await loadData();
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.category_id ?? deleteTarget?.id;
    if (!id) return;
    const success = await deleteItemCategory(id);
    if (success) {
      setDeleteTarget(null);
      await loadData();
    }
  };

  const restaurantName = (restaurantId) => {
    const row = restaurants.find(
      (item) =>
        String(item?.restaurant_id ?? item?.id ?? "") ===
        String(restaurantId ?? ""),
    );
    return row?.restaurant_name || `Restaurant #${restaurantId || "—"}`;
  };

  const categoryName = (categoryId) => {
    if (!categoryId) return "Root category";
    const row = categories.find(
      (item) =>
        String(item?.category_id ?? item?.id ?? "") === String(categoryId),
    );
    return row?.category_name || `Category #${categoryId}`;
  };

  const parentOptions = categories.filter((item) => {
    const id = String(item?.category_id ?? item?.id ?? "");
    const sameRestaurant =
      !form.restaurant_id ||
      String(item?.restaurant_id ?? "") === String(form.restaurant_id);
    return id && id !== String(form.category_id || "") && sameRestaurant;
  });

  return (
    <div className="space-y-3 pt-2">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Item Categories
          </h3>
          <p className="text-[11px] text-slate-500">
            Manage restaurant item categories and parent categories.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadData}
            disabled={itemCategoriesLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={itemCategoriesLoading ? "animate-spin" : ""}
            />{" "}
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-violet-600 px-3 text-xs font-semibold text-white hover:bg-violet-700"
          >
            <Plus size={13} /> Add Category
          </button>
        </div>
      </div>

      {itemCategoriesLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : categories.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center">
          <p className="text-sm font-semibold text-slate-800">
            No item categories found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Add the first category for a restaurant.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {categories.map((category, index) => {
            const id = category?.category_id ?? category?.id ?? index;
            const active =
              String(category?.status || "").toUpperCase() === "ACTIVE";
            return (
              <div
                key={id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {category?.category_name || "Unnamed category"}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
                    >
                      {category?.status || "-"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    ID #{id} · {restaurantName(category?.restaurant_id)} ·
                    Parent: {categoryName(category?.parent_category_id)} ·
                    Order: {category?.display_order || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(category)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-violet-50 hover:text-violet-600"
                  title="Edit"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(category)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 hover:bg-rose-100"
                  title="Delete"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT"
                    ? "Edit Item Category"
                    : "Add Item Category"}
                </h3>
                <p className="text-xs text-slate-500">
                  Bulk Catalogue category details
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={itemCategorySaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant
                  </span>
                  <select
                    name="restaurant_id"
                    value={form.restaurant_id}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="">Select Restaurant</option>
                    {restaurants.map((restaurant) => {
                      const id = restaurant?.restaurant_id ?? restaurant?.id;
                      return (
                        <option key={id} value={id}>
                          {restaurant?.restaurant_name || `Restaurant #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Parent Category
                  </span>
                  <select
                    name="parent_category_id"
                    value={form.parent_category_id}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="">No Parent / Root</option>
                    {parentOptions.map((category) => {
                      const id = category?.category_id ?? category?.id;
                      return (
                        <option key={id} value={id}>
                          {category?.category_name || `Category #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Category Name
                  </span>
                  <input
                    name="category_name"
                    value={form.category_name}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Display Order
                  </span>
                  <input
                    name="display_order"
                    type="number"
                    value={form.display_order}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </span>
                  <textarea
                    name="description"
                    rows={3}
                    value={form.description}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={itemCategorySaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={itemCategorySaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-violet-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {itemCategorySaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {itemCategorySaving
                    ? "Saving..."
                    : editing.mode === "EDIT"
                      ? "Update Category"
                      : "Add Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              Delete Item Category
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Delete <b>{deleteTarget?.category_name || "this category"}</b>?
              This action cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(itemCategoryDeletingId)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(itemCategoryDeletingId)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {itemCategoryDeletingId ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}{" "}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const EMPTY_ITEM_FORM = {
  item_id: "",
  restaurant_id: "",
  item_name: "",
  item_description: "",
  item_type: "",
  image_url: "",
  image_data: "",
  tax_percentage: "",
  addon_price: "",
  less_price: "",
  upgrade_price: "",
  preparation_time_minutes: "",
  status: "ACTIVE",
};

function BulkItemManagement() {
  return (
    <div className="space-y-4 pt-2">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">
          Items & Pricing Management
        </h3>
        <p className="mt-0.5 text-[11px] text-slate-600">
          Lazy hierarchy: Item → Variant → Variant Price → Price Tier.
          Categories load only when opened.
        </p>
      </div>
      <BulkItems />
    </div>
  );
}

function BulkItems() {
  const {
    getRestaurants,
    getItems,
    getItemCategories,
    getItemCategoryMappings,
    getItemVariants,
    getItemVariantPrices,
    getItemPriceTiers,
    getCatalogues,
    insertItemVariant,
    updateItemVariant,
    deleteItemVariant,
    insertItemVariantPrice,
    updateItemVariantPrice,
    deleteItemVariantPrice,
    insertItemPriceTier,
    updateItemPriceTier,
    deleteItemPriceTier,
    itemVariantSaving,
    itemVariantPriceSaving,
    itemPriceTierSaving,
    insertItem,
    updateItem,
    deleteItem,
    itemSaving,
    itemsLoading,
    itemDeletingId,
    itemImageUploading,
  } = useBulkCatalogue();

  const [restaurants, setRestaurants] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_ITEM_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedItemId, setExpandedItemId] = useState("");
  const [expandedVariantId, setExpandedVariantId] = useState("");
  const [expandedPriceId, setExpandedPriceId] = useState("");
  const [variantsByItem, setVariantsByItem] = useState({});
  const [pricesByVariant, setPricesByVariant] = useState({});
  const [tiersByPrice, setTiersByPrice] = useState({});
  const [categoriesByItem, setCategoriesByItem] = useState({});
  const [loadingNode, setLoadingNode] = useState("");
  const [nestedEditor, setNestedEditor] = useState(null);
  const [nestedForm, setNestedForm] = useState({});
  const [catalogues, setCatalogues] = useState([]);

  const openVariantEditor = (itemId, row = null) => {
    setNestedEditor({
      type: "variant",
      mode: row ? "EDIT" : "ADD",
      parentId: String(itemId),
    });
    setNestedForm(
      row
        ? { ...row }
        : {
            item_id: String(itemId),
            variant_name: "",
            unit_type: "",
            unit_value: "",
            serves_people: "",
            sku: "",
            status: "ACTIVE",
            fk_package_id: "",
          },
    );
  };

  const openPriceEditor = async (variantId, row = null) => {
    if (!catalogues.length) {
      const rows = await getCatalogues("");
      setCatalogues(Array.isArray(rows) ? rows : []);
    }
    setNestedEditor({
      type: "price",
      mode: row ? "EDIT" : "ADD",
      parentId: String(variantId),
    });
    setNestedForm(
      row
        ? {
            ...row,
            has_minimum_order:
              row?.has_minimum_order === true ||
              String(row?.has_minimum_order).toUpperCase() === "Y"
                ? "Y"
                : "N",
          }
        : {
            variant_id: String(variantId),
            catalogue_id: "",
            order_type: "home_delivery",
            price: "",
            has_minimum_order: "N",
            minimum_quantity: "",
            minimum_order_unit: "",
            maximum_quantity: "",
            quantity_step: "",
            valid_from: "",
            valid_to: "",
            status: "ACTIVE",
          },
    );
  };

  const openTierEditor = (variantPriceId, row = null) => {
    setNestedEditor({
      type: "tier",
      mode: row ? "EDIT" : "ADD",
      parentId: String(variantPriceId),
    });
    setNestedForm(
      row
        ? { ...row }
        : {
            variant_price_id: String(variantPriceId),
            minimum_quantity: "",
            maximum_quantity: "",
            price_type: "FIXED",
            price: "",
          },
    );
  };

  const refreshVariants = async (itemId) => {
    const rows = await getItemVariants(itemId);
    setVariantsByItem((prev) => ({
      ...prev,
      [String(itemId)]: Array.isArray(rows) ? rows : [],
    }));
  };
  const refreshPrices = async (variantId) => {
    const rows = await getItemVariantPrices(variantId);
    setPricesByVariant((prev) => ({
      ...prev,
      [String(variantId)]: Array.isArray(rows) ? rows : [],
    }));
  };
  const refreshTiers = async (priceId) => {
    const rows = await getItemPriceTiers(priceId);
    setTiersByPrice((prev) => ({
      ...prev,
      [String(priceId)]: Array.isArray(rows) ? rows : [],
    }));
  };

  const submitNested = async (e) => {
    e.preventDefault();
    if (!nestedEditor) return;
    let ok = false;
    if (nestedEditor.type === "variant") {
      ok =
        nestedEditor.mode === "EDIT"
          ? await updateItemVariant(nestedForm)
          : await insertItemVariant(nestedForm);
      if (ok) await refreshVariants(nestedEditor.parentId);
    } else if (nestedEditor.type === "price") {
      ok =
        nestedEditor.mode === "EDIT"
          ? await updateItemVariantPrice(nestedForm)
          : await insertItemVariantPrice(nestedForm);
      if (ok) await refreshPrices(nestedEditor.parentId);
    } else if (nestedEditor.type === "tier") {
      ok =
        nestedEditor.mode === "EDIT"
          ? await updateItemPriceTier(nestedForm)
          : await insertItemPriceTier(nestedForm);
      if (ok) await refreshTiers(nestedEditor.parentId);
    }
    if (ok) {
      setNestedEditor(null);
      setNestedForm({});
    }
  };

  const removeVariant = async (itemId, variantId) => {
    if (window.confirm("Delete this variant?")) {
      const ok = await deleteItemVariant(variantId);
      if (ok) await refreshVariants(itemId);
    }
  };
  const removePrice = async (variantId, priceId) => {
    if (window.confirm("Delete this variant price?")) {
      const ok = await deleteItemVariantPrice(priceId);
      if (ok) await refreshPrices(variantId);
    }
  };
  const removeTier = async (priceId, tierId) => {
    if (window.confirm("Delete this price tier?")) {
      const ok = await deleteItemPriceTier(tierId);
      if (ok) await refreshTiers(priceId);
    }
  };

  const loadData = async () => {
    // Initial tab load intentionally calls ONLY itemsGet.
    const itemRows = await getItems("");
    setItems(Array.isArray(itemRows) ? itemRows : []);
    setExpandedItemId("");
    setExpandedVariantId("");
    setExpandedPriceId("");
    setVariantsByItem({});
    setPricesByVariant({});
    setTiersByPrice({});
    setCategoriesByItem({});
  };

  const toggleItem = async (itemId) => {
    const id = String(itemId ?? "");
    if (expandedItemId === id) {
      setExpandedItemId("");
      return;
    }
    setExpandedItemId(id);
    if (Object.prototype.hasOwnProperty.call(variantsByItem, id)) return;
    setLoadingNode(`item:${id}`);
    try {
      const rows = await getItemVariants(id);
      setVariantsByItem((prev) => ({
        ...prev,
        [id]: Array.isArray(rows) ? rows : [],
      }));
    } finally {
      setLoadingNode("");
    }
  };

  const loadCategoriesForItem = async (itemId) => {
    const id = String(itemId ?? "");
    if (Object.prototype.hasOwnProperty.call(categoriesByItem, id)) return;
    setLoadingNode(`categories:${id}`);
    try {
      const [mappings, categories] = await Promise.all([
        getItemCategoryMappings(id, ""),
        getItemCategories(""),
      ]);
      const categoryRows = Array.isArray(categories) ? categories : [];
      const names = (Array.isArray(mappings) ? mappings : []).map((m) => {
        const categoryId = String(m?.category_id ?? "");
        const category = categoryRows.find(
          (c) => String(c?.category_id ?? c?.id ?? "") === categoryId,
        );
        return {
          ...m,
          category_name:
            category?.category_name || `Category #${categoryId || "—"}`,
        };
      });
      setCategoriesByItem((prev) => ({ ...prev, [id]: names }));
    } finally {
      setLoadingNode("");
    }
  };

  const toggleVariant = async (variantId) => {
    const id = String(variantId ?? "");
    if (expandedVariantId === id) {
      setExpandedVariantId("");
      return;
    }
    setExpandedVariantId(id);
    if (Object.prototype.hasOwnProperty.call(pricesByVariant, id)) return;
    setLoadingNode(`variant:${id}`);
    try {
      const rows = await getItemVariantPrices(id);
      setPricesByVariant((prev) => ({
        ...prev,
        [id]: Array.isArray(rows) ? rows : [],
      }));
    } finally {
      setLoadingNode("");
    }
  };

  const togglePrice = async (variantPriceId) => {
    const id = String(variantPriceId ?? "");
    if (expandedPriceId === id) {
      setExpandedPriceId("");
      return;
    }
    setExpandedPriceId(id);
    if (Object.prototype.hasOwnProperty.call(tiersByPrice, id)) return;
    setLoadingNode(`price:${id}`);
    try {
      const rows = await getItemPriceTiers(id);
      setTiersByPrice((prev) => ({
        ...prev,
        [id]: Array.isArray(rows) ? rows : [],
      }));
    } finally {
      setLoadingNode("");
    }
  };

  const ensureRestaurants = async () => {
    if (restaurants.length) return restaurants;
    const rows = await getRestaurants("");
    const next = Array.isArray(rows) ? rows : [];
    setRestaurants(next);
    return next;
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm((prev) => ({ ...prev, image_data: String(reader.result || "") }));
    };
    reader.readAsDataURL(file);
  };

  const openAdd = async () => {
    const restaurantRows = await ensureRestaurants();
    setEditing({ mode: "ADD" });
    setForm({
      ...EMPTY_ITEM_FORM,
      restaurant_id: String(
        restaurantRows?.[0]?.restaurant_id ?? restaurantRows?.[0]?.id ?? "",
      ),
    });
  };

  const openEdit = async (item) => {
    await ensureRestaurants();
    setEditing({ mode: "EDIT" });
    setForm({
      item_id: String(item?.item_id ?? item?.id ?? ""),
      restaurant_id: String(item?.restaurant_id ?? ""),
      item_name: String(item?.item_name ?? ""),
      item_description: String(item?.item_description ?? ""),
      item_type: String(item?.item_type ?? ""),
      image_url: String(item?.image_url ?? ""),
      image_data: "",
      tax_percentage: String(item?.tax_percentage ?? ""),
      addon_price: String(item?.addon_price ?? ""),
      less_price: String(item?.less_price ?? ""),
      upgrade_price: String(item?.upgrade_price ?? ""),
      preparation_time_minutes: String(item?.preparation_time_minutes ?? ""),
      status: String(item?.status ?? "ACTIVE"),
    });
  };

  const closeModal = () => {
    if (!itemSaving && !itemImageUploading) {
      setEditing(null);
      setForm(EMPTY_ITEM_FORM);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success =
      editing?.mode === "EDIT"
        ? await updateItem(form)
        : await insertItem(form);
    if (success) {
      closeModal();
      await loadData();
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.item_id ?? deleteTarget?.id;
    if (!id) return;
    const success = await deleteItem(id);
    if (success) {
      setDeleteTarget(null);
      await loadData();
    }
  };

  const restaurantName = (restaurantId) => {
    const row = restaurants.find(
      (r) =>
        String(r?.restaurant_id ?? r?.id ?? "") === String(restaurantId ?? ""),
    );
    return row?.restaurant_name || `Restaurant #${restaurantId || "—"}`;
  };

  const imagePreview = form.image_data || form.image_url;
  const busy = itemSaving || itemImageUploading;

  return (
    <div className="space-y-3 pt-2">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Bulk Items</h3>
          <p className="text-[11px] text-slate-500">
            Manage items. New image is uploaded first, then returned URL is sent
            in image_url.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadData}
            disabled={itemsLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={itemsLoading ? "animate-spin" : ""}
            />{" "}
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-amber-600 px-3 text-xs font-semibold text-white hover:bg-amber-700"
          >
            <Plus size={13} /> Add Item
          </button>
        </div>
      </div>

      {itemsLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center">
          <p className="text-sm font-semibold text-slate-800">No items found</p>
          <p className="mt-1 text-xs text-slate-500">
            Add the first Bulk Catalogue item.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {items.map((item, index) => {
            const id = String(item?.item_id ?? item?.id ?? index);
            const active =
              String(item?.status || "").toUpperCase() === "ACTIVE";
            const expanded = expandedItemId === id;
            const variants = variantsByItem[id] || [];
            return (
              <div
                key={id}
                className="border-b border-slate-100 last:border-b-0"
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggleItem(id)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    {loadingNode === `item:${id}` ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : expanded ? (
                      <ChevronDown size={15} />
                    ) : (
                      <ChevronRight size={15} />
                    )}
                  </button>
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-slate-100 bg-slate-50">
                    {item?.image_url ? (
                      <img
                        src={item.image_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-[9px] text-slate-400">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {item?.item_name || "Unnamed item"}
                      </p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
                      >
                        {item?.status || "-"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      ID #{id} · {item?.item_type || "No type"} · Prep{" "}
                      {item?.preparation_time_minutes || "—"} min
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-amber-50 hover:text-amber-600"
                  >
                    <Edit3 size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(item)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>

                {expanded ? (
                  <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 pl-14">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-semibold text-slate-700">
                          Variants ({variants.length})
                        </p>
                        <button
                          type="button"
                          onClick={() => openVariantEditor(id)}
                          className="rounded-md bg-emerald-600 px-2 py-1 text-[10px] font-semibold text-white"
                        >
                          <Plus size={10} className="mr-1 inline" />
                          Add Variant
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => loadCategoriesForItem(id)}
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-semibold text-slate-600"
                      >
                        {loadingNode === `categories:${id}`
                          ? "Loading categories..."
                          : categoriesByItem[id]
                            ? `Categories (${categoriesByItem[id].length})`
                            : "Load Categories"}
                      </button>
                    </div>
                    {categoriesByItem[id] ? (
                      <div className="mb-3 flex flex-wrap gap-1.5">
                        {categoriesByItem[id].length ? (
                          categoriesByItem[id].map((m, i) => (
                            <span
                              key={`${m?.category_id}-${i}`}
                              className="rounded-full bg-violet-50 px-2 py-1 text-[10px] font-medium text-violet-700"
                            >
                              {m.category_name}
                            </span>
                          ))
                        ) : (
                          <span className="text-[10px] text-slate-400">
                            No category mapping
                          </span>
                        )}
                      </div>
                    ) : null}
                    {loadingNode === `item:${id}` ? (
                      <div className="py-4 text-xs text-slate-500">
                        Loading variants...
                      </div>
                    ) : variants.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-4 text-xs text-slate-500">
                        No variants found for this item.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {variants.map((variant) => {
                          const variantId = String(
                            variant?.variant_id ?? variant?.id ?? "",
                          );
                          const variantExpanded =
                            expandedVariantId === variantId;
                          const prices = pricesByVariant[variantId] || [];
                          return (
                            <div
                              key={variantId}
                              className="rounded-lg border border-slate-200 bg-white"
                            >
                              <button
                                type="button"
                                onClick={() => toggleVariant(variantId)}
                                className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
                              >
                                {loadingNode === `variant:${variantId}` ? (
                                  <Loader2 size={13} className="animate-spin" />
                                ) : variantExpanded ? (
                                  <ChevronDown size={14} />
                                ) : (
                                  <ChevronRight size={14} />
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-semibold text-slate-800">
                                    {variant?.variant_name ||
                                      `Variant #${variantId}`}
                                  </p>
                                  <p className="text-[10px] text-slate-500">
                                    {variant?.unit_value || "—"}{" "}
                                    {variant?.unit_type || ""} · Serves{" "}
                                    {variant?.serves_people || "—"} · SKU{" "}
                                    {variant?.sku || "—"}
                                  </p>
                                </div>
                              </button>
                              <div className="flex justify-end gap-1 border-t border-slate-50 px-3 py-1.5">
                                <button
                                  type="button"
                                  onClick={() => openVariantEditor(id, variant)}
                                  className="rounded border border-slate-200 px-2 py-1 text-[9px] font-semibold text-slate-600"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeVariant(id, variantId)}
                                  className="rounded border border-rose-100 px-2 py-1 text-[9px] font-semibold text-rose-600"
                                >
                                  Delete
                                </button>
                              </div>
                              {variantExpanded ? (
                                <div className="border-t border-slate-100 px-3 py-2 pl-8">
                                  <div className="mb-2 flex items-center justify-between">
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                      Variant Prices
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() => openPriceEditor(variantId)}
                                      className="rounded bg-blue-600 px-2 py-1 text-[9px] font-semibold text-white"
                                    >
                                      + Add Price
                                    </button>
                                  </div>
                                  {prices.length === 0 ? (
                                    <p className="text-[10px] text-slate-400">
                                      No prices found.
                                    </p>
                                  ) : (
                                    <div className="space-y-1.5">
                                      {prices.map((price) => {
                                        const priceId = String(
                                          price?.variant_price_id ??
                                            price?.id ??
                                            "",
                                        );
                                        const priceExpanded =
                                          expandedPriceId === priceId;
                                        const tiers =
                                          tiersByPrice[priceId] || [];
                                        return (
                                          <div
                                            key={priceId}
                                            className="rounded-md border border-slate-100 bg-slate-50"
                                          >
                                            <button
                                              type="button"
                                              onClick={() =>
                                                togglePrice(priceId)
                                              }
                                              className="flex w-full items-center gap-2 px-2.5 py-2 text-left"
                                            >
                                              {loadingNode ===
                                              `price:${priceId}` ? (
                                                <Loader2
                                                  size={12}
                                                  className="animate-spin"
                                                />
                                              ) : priceExpanded ? (
                                                <ChevronDown size={13} />
                                              ) : (
                                                <ChevronRight size={13} />
                                              )}
                                              <span className="flex-1 text-[11px] font-semibold text-slate-700">
                                                ₹{price?.price ?? "—"} ·{" "}
                                                {price?.order_type ||
                                                  "No order type"}
                                              </span>
                                              <span className="text-[9px] text-slate-400">
                                                #{priceId}
                                              </span>
                                            </button>
                                            <div className="flex justify-end gap-1 px-2 pb-1.5">
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  openPriceEditor(
                                                    variantId,
                                                    price,
                                                  )
                                                }
                                                className="rounded border border-slate-200 px-2 py-0.5 text-[9px] text-slate-600"
                                              >
                                                Edit
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  removePrice(
                                                    variantId,
                                                    priceId,
                                                  )
                                                }
                                                className="rounded border border-rose-100 px-2 py-0.5 text-[9px] text-rose-600"
                                              >
                                                Delete
                                              </button>
                                            </div>
                                            {priceExpanded ? (
                                              <div className="border-t border-slate-100 bg-white px-3 py-2">
                                                <div className="mb-1.5 flex items-center justify-between">
                                                  <p className="text-[10px] font-semibold text-slate-500">
                                                    Price Tiers
                                                  </p>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      openTierEditor(priceId)
                                                    }
                                                    className="rounded bg-violet-600 px-2 py-1 text-[9px] font-semibold text-white"
                                                  >
                                                    + Add Tier
                                                  </button>
                                                </div>
                                                {tiers.length === 0 ? (
                                                  <p className="text-[10px] text-slate-400">
                                                    No price tiers found.
                                                  </p>
                                                ) : (
                                                  <div className="space-y-1">
                                                    {tiers.map((tier, ti) => (
                                                      <div
                                                        key={
                                                          tier?.item_price_tier_id ??
                                                          ti
                                                        }
                                                        className="flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1.5 text-[10px] text-slate-600"
                                                      >
                                                        <span className="flex-1">
                                                          {tier?.minimum_quantity ??
                                                            "—"}{" "}
                                                          -{" "}
                                                          {tier?.maximum_quantity ??
                                                            "—"}{" "}
                                                          ·{" "}
                                                          {tier?.price_type ||
                                                            "Tier"}
                                                        </span>
                                                        <b>
                                                          ₹{tier?.price ?? "—"}
                                                        </b>
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            openTierEditor(
                                                              priceId,
                                                              tier,
                                                            )
                                                          }
                                                          className="text-slate-500"
                                                        >
                                                          Edit
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            removeTier(
                                                              priceId,
                                                              tier?.item_price_tier_id,
                                                            )
                                                          }
                                                          className="text-rose-600"
                                                        >
                                                          Delete
                                                        </button>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {editing ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT" ? "Edit Item" : "Add Item"}
                </h3>
                <p className="text-xs text-slate-500">
                  Bulk Catalogue item details
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={busy}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant
                  </span>
                  <select
                    name="restaurant_id"
                    value={form.restaurant_id}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="">Select Restaurant</option>
                    {restaurants.map((r) => {
                      const id = r?.restaurant_id ?? r?.id;
                      return (
                        <option key={id} value={id}>
                          {r?.restaurant_name || `Restaurant #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Item Name
                  </span>
                  <input
                    name="item_name"
                    value={form.item_name}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Item Type
                  </span>
                  <input
                    name="item_type"
                    value={form.item_type}
                    onChange={handleChange}
                    disabled={busy}
                    placeholder="e.g. VEG / NON_VEG"
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </span>
                  <textarea
                    name="item_description"
                    rows={3}
                    value={form.item_description}
                    onChange={handleChange}
                    disabled={busy}
                    className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                  />
                </label>

                <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
                  <span className="mb-2 block text-xs font-semibold text-slate-700">
                    Item Image
                  </span>
                  <div className="flex items-center gap-3">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="h-20 w-20 rounded-xl border border-slate-200 object-cover"
                      />
                    ) : (
                      <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-dashed border-slate-300 text-[10px] text-slate-400">
                        No image
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={busy}
                        className="block w-full text-xs text-slate-600"
                      />
                      <p className="mt-1 text-[10px] text-slate-400">
                        On Save: PHP upload API runs first. Its image_url is
                        then sent to itemsInsert/itemsUpdate.
                      </p>
                    </div>
                  </div>
                </div>

                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Tax %
                  </span>
                  <input
                    name="tax_percentage"
                    type="number"
                    step="0.01"
                    value={form.tax_percentage}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Preparation Time (minutes)
                  </span>
                  <input
                    name="preparation_time_minutes"
                    type="number"
                    value={form.preparation_time_minutes}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Addon Price
                  </span>
                  <input
                    name="addon_price"
                    type="number"
                    step="0.01"
                    value={form.addon_price}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Less Price
                  </span>
                  <input
                    name="less_price"
                    type="number"
                    step="0.01"
                    value={form.less_price}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Upgrade Price
                  </span>
                  <input
                    name="upgrade_price"
                    type="number"
                    step="0.01"
                    value={form.upgrade_price}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
              </div>
              <div className="sticky bottom-0 flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={busy}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-amber-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {itemImageUploading
                    ? "Uploading image..."
                    : itemSaving
                      ? "Saving..."
                      : editing.mode === "EDIT"
                        ? "Update Item"
                        : "Add Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {nestedEditor ? (
        <div className="fixed inset-0 z-[12500] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <form
            onSubmit={submitNested}
            className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {nestedEditor.mode === "EDIT" ? "Edit" : "Add"}{" "}
                  {nestedEditor.type === "variant"
                    ? "Variant"
                    : nestedEditor.type === "price"
                      ? "Variant Price"
                      : "Price Tier"}
                </h3>
                <p className="text-xs text-slate-500">
                  Parent ID #{nestedEditor.parentId}
                </p>
              </div>
              <button type="button" onClick={() => setNestedEditor(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
              {nestedEditor.type === "variant" && (
                <>
                  <label className="text-xs font-medium">
                    Variant Name
                    <input
                      required
                      value={nestedForm.variant_name || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          variant_name: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Unit Type
                    <input
                      value={nestedForm.unit_type || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          unit_type: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Unit Value
                    <input
                      value={nestedForm.unit_value || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          unit_value: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Serves People
                    <input
                      value={nestedForm.serves_people || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          serves_people: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    SKU
                    <input
                      value={nestedForm.sku || ""}
                      onChange={(e) =>
                        setNestedForm({ ...nestedForm, sku: e.target.value })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Package ID
                    <input
                      value={nestedForm.fk_package_id || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          fk_package_id: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                </>
              )}
              {nestedEditor.type === "price" && (
                <>
                  <label className="text-xs font-medium">
                    Catalogue
                    <select
                      required
                      value={nestedForm.catalogue_id || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          catalogue_id: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    >
                      <option value="">Select catalogue</option>
                      {catalogues.map((c) => (
                        <option key={c.catalogue_id} value={c.catalogue_id}>
                          {c.catalogue_name || `Catalogue #${c.catalogue_id}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-medium">
                    Order Type
                    <input
                      required
                      value={nestedForm.order_type || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          order_type: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Price
                    <input
                      required
                      type="number"
                      step="0.01"
                      value={nestedForm.price || ""}
                      onChange={(e) =>
                        setNestedForm({ ...nestedForm, price: e.target.value })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Minimum Order
                    <select
                      value={nestedForm.has_minimum_order || "N"}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          has_minimum_order: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    >
                      <option value="N">No</option>
                      <option value="Y">Yes</option>
                    </select>
                  </label>
                  <label className="text-xs font-medium">
                    Minimum Qty
                    <input
                      value={nestedForm.minimum_quantity || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          minimum_quantity: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Minimum Unit
                    <input
                      value={nestedForm.minimum_order_unit || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          minimum_order_unit: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Maximum Qty
                    <input
                      value={nestedForm.maximum_quantity || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          maximum_quantity: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Quantity Step
                    <input
                      value={nestedForm.quantity_step || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          quantity_step: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Valid From
                    <input
                      type="date"
                      value={(nestedForm.valid_from || "").slice(0, 10)}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          valid_from: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Valid To
                    <input
                      type="date"
                      value={(nestedForm.valid_to || "").slice(0, 10)}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          valid_to: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                </>
              )}
              {nestedEditor.type === "tier" && (
                <>
                  <label className="text-xs font-medium">
                    Minimum Quantity
                    <input
                      required
                      type="number"
                      value={nestedForm.minimum_quantity || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          minimum_quantity: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Maximum Quantity
                    <input
                      type="number"
                      value={nestedForm.maximum_quantity || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          maximum_quantity: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Price Type
                    <input
                      required
                      value={nestedForm.price_type || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          price_type: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Price
                    <input
                      required
                      type="number"
                      step="0.01"
                      value={nestedForm.price || ""}
                      onChange={(e) =>
                        setNestedForm({ ...nestedForm, price: e.target.value })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                </>
              )}
              <label className="text-xs font-medium">
                Status
                <select
                  value={nestedForm.status || "ACTIVE"}
                  onChange={(e) =>
                    setNestedForm({ ...nestedForm, status: e.target.value })
                  }
                  className="mt-1 h-9 w-full rounded-lg border px-3"
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t px-4 py-3">
              <button
                type="button"
                onClick={() => setNestedEditor(null)}
                className="h-9 rounded-lg border px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  itemVariantSaving ||
                  itemVariantPriceSaving ||
                  itemPriceTierSaving
                }
                className="h-9 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {nestedEditor.mode === "EDIT" ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              Delete Item
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Delete <b>{deleteTarget?.item_name || "this item"}</b>? This
              action cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(itemDeletingId)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(itemDeletingId)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {itemDeletingId ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}{" "}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const EMPTY_MAPPING_FORM = { item_id: "", category_id: "", is_primary: "N" };

function BulkItemCategoryMappings() {
  const {
    getItems,
    getItemCategories,
    getItemCategoryMappings,
    insertItemCategoryMapping,
    updateItemCategoryMapping,
    deleteItemCategoryMapping,
    itemCategoryMappingSaving,
    itemCategoryMappingsLoading,
    itemCategoryMappingDeletingKey,
  } = useBulkCatalogue();

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [form, setForm] = useState(EMPTY_MAPPING_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadMappings = async () => {
    const [mappingRows, itemRows, categoryRows] = await Promise.all([
      getItemCategoryMappings("", ""),
      getItems(""),
      getItemCategories(""),
    ]);
    setMappings(Array.isArray(mappingRows) ? mappingRows : []);
    setItems(Array.isArray(itemRows) ? itemRows : []);
    setCategories(Array.isArray(categoryRows) ? categoryRows : []);
  };

  useEffect(() => {
    loadMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureMappingOptions = async () => {
    if (items.length && categories.length)
      return { itemRows: items, categoryRows: categories };
    const [itemRowsRaw, categoryRowsRaw] = await Promise.all([
      items.length ? Promise.resolve(items) : getItems(""),
      categories.length ? Promise.resolve(categories) : getItemCategories(""),
    ]);
    const itemRows = Array.isArray(itemRowsRaw) ? itemRowsRaw : [];
    const categoryRows = Array.isArray(categoryRowsRaw) ? categoryRowsRaw : [];
    setItems(itemRows);
    setCategories(categoryRows);
    return { itemRows, categoryRows };
  };

  const itemName = (id) =>
    items.find((x) => String(x?.item_id ?? x?.id ?? "") === String(id ?? ""))
      ?.item_name || `Item #${id || "—"}`;
  const categoryName = (id) =>
    categories.find(
      (x) => String(x?.category_id ?? x?.id ?? "") === String(id ?? ""),
    )?.category_name || `Category #${id || "—"}`;

  const openAdd = async () => {
    const { itemRows, categoryRows } = await ensureMappingOptions();
    setEditing({ mode: "ADD" });
    setForm({
      item_id: String(itemRows?.[0]?.item_id ?? itemRows?.[0]?.id ?? ""),
      category_id: String(
        categoryRows?.[0]?.category_id ?? categoryRows?.[0]?.id ?? "",
      ),
      is_primary: "N",
    });
  };

  const openEdit = async (row) => {
    await ensureMappingOptions();
    setEditing({ mode: "EDIT" });
    setForm({
      item_id: String(row?.item_id ?? ""),
      category_id: String(row?.category_id ?? ""),
      is_primary: String(row?.is_primary ?? "N"),
    });
  };

  const closeModal = () => {
    if (itemCategoryMappingSaving) return;
    setEditing(null);
    setForm(EMPTY_MAPPING_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok =
      editing?.mode === "EDIT"
        ? await updateItemCategoryMapping(form)
        : await insertItemCategoryMapping(form);
    if (ok) {
      closeModal();
      await loadMappings();
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const ok = await deleteItemCategoryMapping(
      deleteTarget?.item_id,
      deleteTarget?.category_id,
    );
    if (ok) {
      setDeleteTarget(null);
      await loadMappings();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Item Category Mapping
          </h3>
          <p className="text-[11px] text-slate-500">
            Map items to categories and select the primary category.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadMappings}
            disabled={itemCategoryMappingsLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={itemCategoryMappingsLoading ? "animate-spin" : ""}
            />{" "}
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
          >
            <Plus size={13} /> Add Mapping
          </button>
        </div>
      </div>

      {itemCategoryMappingsLoading ? (
        <div className="h-24 animate-pulse rounded-xl border border-slate-200 bg-white" />
      ) : mappings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">
          No item category mappings found.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {mappings.map((row, index) => {
            const key = `${row?.item_id}-${row?.category_id}-${index}`;
            const primary = ["Y", "YES", "TRUE", "1"].includes(
              String(row?.is_primary ?? "").toUpperCase(),
            );
            return (
              <div
                key={key}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">
                      {itemName(row?.item_id)}
                    </p>
                    <span className="text-xs text-slate-400">→</span>
                    <p className="text-sm font-medium text-slate-700">
                      {categoryName(row?.category_id)}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${primary ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                    >
                      {primary ? "PRIMARY" : "SECONDARY"}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Item #{row?.item_id || "—"} · Category #
                    {row?.category_id || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT" ? "Edit Mapping" : "Add Mapping"}
                </h3>
                <p className="text-xs text-slate-500">
                  Item and category relation
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={itemCategoryMappingSaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Item
                  </span>
                  <select
                    value={form.item_id}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, item_id: e.target.value }))
                    }
                    disabled={itemCategoryMappingSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    {items.map((row) => {
                      const id = row?.item_id ?? row?.id;
                      return (
                        <option key={id} value={id}>
                          {row?.item_name || `Item #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Category
                  </span>
                  <select
                    value={form.category_id}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, category_id: e.target.value }))
                    }
                    disabled={itemCategoryMappingSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    {categories.map((row) => {
                      const id = row?.category_id ?? row?.id;
                      return (
                        <option key={id} value={id}>
                          {row?.category_name || `Category #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Primary Category
                  </span>
                  <select
                    value={form.is_primary}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, is_primary: e.target.value }))
                    }
                    disabled={itemCategoryMappingSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={itemCategoryMappingSaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    itemCategoryMappingSaving ||
                    !form.item_id ||
                    !form.category_id
                  }
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {itemCategoryMappingSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {itemCategoryMappingSaving
                    ? "Saving..."
                    : editing.mode === "EDIT"
                      ? "Update Mapping"
                      : "Add Mapping"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              Delete Mapping
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Remove <b>{itemName(deleteTarget?.item_id)}</b> from{" "}
              <b>{categoryName(deleteTarget?.category_id)}</b>?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(itemCategoryMappingDeletingKey)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(itemCategoryMappingDeletingKey)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {itemCategoryMappingDeletingKey ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
